import React, { useEffect, useMemo, useState } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { VALID_TIME_SIGNATURE_DENOMINATORS } from '@chordbaker/music-theory';
import { TimeSignature } from '../types';
import CloseIcon from './icons/CloseIcon';

const NUMERATOR_OPTIONS = Array.from({ length: 16 }, (_, i) => i + 1);
const DEFAULT_NUMERATOR = 4;
const DEFAULT_DENOMINATOR = 4;

const createStyles = (theme: Theme) => ({
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    overflowY: 'auto' as const,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
    zIndex: 50,
    transition: `opacity ${theme.motion.duration.normal}ms ${theme.motion.easing.standard}`,
    backgroundColor: theme.colors.modalOverlay,
    opacity: 1,
    pointerEvents: 'auto' as const,
  },
  overlayHidden: {
    backgroundColor: 'transparent',
    opacity: 0,
    pointerEvents: 'none' as const,
  },
  modalContent: {
    backgroundColor: theme.colors.modalBackground,
    borderRadius: theme.radii.xl,
    boxShadow: theme.shadows.modal,
    width: '100%',
    maxWidth: '32rem',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column' as const,
    transition: `all ${theme.motion.duration.normal}ms ${theme.motion.easing.standard}`,
    opacity: 1,
    transform: 'scale(1)',
  },
  modalContentHidden: {
    opacity: 0,
    transform: 'scale(0.95)',
  },
  header: {
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.xl,
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  title: {
    fontSize: theme.typography.sizes.xxl,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    margin: 0,
  },
  closeButton: {
    color: theme.colors.textSecondary,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing.xs,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
  },
  previewBox: {
    backgroundColor: theme.isDark ? theme.colors.surfaceSecondary : theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.lg,
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    minWidth: 80,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewText: {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: theme.typography.sizes.xxxl,
    color: theme.colors.text,
    fontWeight: theme.typography.weights.semiBold,
    lineHeight: 1.1,
    textAlign: 'center' as const,
  },
  scrollArea: {
    overflowY: 'auto' as const,
    flex: 1,
  },
  sectionContainer: {
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
  },
  sectionLabel: {
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    color: theme.colors.text,
    marginBottom: theme.spacing.sm,
    margin: 0,
  },
  optionGrid: {
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
  },
  optionButton: {
    backgroundColor: theme.colors.surfaceSecondary,
    border: 'none',
    borderRadius: theme.radii.md,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    minWidth: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.text,
    fontWeight: theme.typography.weights.normal,
    cursor: 'pointer',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  optionButtonSelected: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.textInverse,
  },
  footer: {
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: theme.spacing.xl,
    borderTop: `1px solid ${theme.colors.border}`,
    gap: theme.spacing.lg,
  },
  clearButton: {
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    backgroundColor: theme.colors.surfaceSecondary,
    borderRadius: theme.radii.md,
    border: 'none',
    color: theme.colors.text,
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    cursor: 'pointer',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  applyButton: {
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.radii.md,
    border: 'none',
    color: theme.colors.textInverse,
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    cursor: 'pointer',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
});

export interface TimeSignatureEditorModalProps {
  isOpen: boolean;
  initialValue: TimeSignature | null;
  onClose: () => void;
  onApply: (value: TimeSignature) => void;
  onClear: () => void;
}

const TimeSignatureEditorModal: React.FC<TimeSignatureEditorModalProps> = ({
  isOpen,
  initialValue,
  onClose,
  onApply,
  onClear,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [showContent, setShowContent] = useState(false);
  const [numerator, setNumerator] = useState<number>(
    initialValue?.numerator ?? DEFAULT_NUMERATOR,
  );
  const [denominator, setDenominator] = useState<number>(
    initialValue?.denominator ?? DEFAULT_DENOMINATOR,
  );

  // Reset local state to match the row's current value each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setNumerator(initialValue?.numerator ?? DEFAULT_NUMERATOR);
      setDenominator(initialValue?.denominator ?? DEFAULT_DENOMINATOR);
      setShowContent(true);
    } else {
      setShowContent(false);
    }
  }, [isOpen, initialValue]);

  if (!isOpen && !showContent) {
    return null;
  }

  const handleApply = () => {
    onApply({ numerator, denominator });
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      style={{
        ...styles.overlay,
        ...(isOpen ? {} : styles.overlayHidden),
      }}
      onClick={handleOverlayClick}
    >
      <div
        style={{
          ...styles.modalContent,
          ...(showContent && isOpen ? {} : styles.modalContentHidden),
        }}
      >
        <div style={styles.header}>
          <h2 style={styles.title}>Time signature</h2>
          <button
            type="button"
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close modal"
          >
            <CloseIcon size={20} color="currentColor" />
          </button>
        </div>

        <div style={styles.previewContainer}>
          <div style={styles.previewBox}>
            <span style={styles.previewText}>{numerator}</span>
            <span style={styles.previewText}>{denominator}</span>
          </div>
        </div>

        <div style={styles.scrollArea}>
          <div style={styles.sectionContainer}>
            <p style={styles.sectionLabel}>Beats per bar</p>
            <div style={styles.optionGrid}>
              {NUMERATOR_OPTIONS.map((value) => {
                const selected = value === numerator;
                return (
                  <button
                    key={`num-${value}`}
                    type="button"
                    onClick={() => setNumerator(value)}
                    style={{
                      ...styles.optionButton,
                      ...(selected ? styles.optionButtonSelected : {}),
                    }}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={styles.sectionContainer}>
            <p style={styles.sectionLabel}>Note value</p>
            <div style={styles.optionGrid}>
              {VALID_TIME_SIGNATURE_DENOMINATORS.map((value) => {
                const selected = value === denominator;
                return (
                  <button
                    key={`den-${value}`}
                    type="button"
                    onClick={() => setDenominator(value)}
                    style={{
                      ...styles.optionButton,
                      ...(selected ? styles.optionButtonSelected : {}),
                    }}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          {initialValue != null && (
            <button type="button" onClick={onClear} style={styles.clearButton}>
              Clear
            </button>
          )}
          <button type="button" onClick={handleApply} style={styles.applyButton}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeSignatureEditorModal;
